import React from 'react';
import styled, { css } from 'styled-components';
import {
  brandColours,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  standardColours,
  standardTransition,
  visuallyHidden,
} from '../styles';
import { Button, Link } from './ui';

const StyledServicesNav = styled.div``;

const StyledContent = styled.div`
  ${minBreakpointQuery.mlarge`
    display: grid;
    gap: 20px;
    grid-template-columns: 1.4fr 1.6fr 1fr;
    align-items: start;
  `}

  ${minBreakpointQuery.large`
    column-gap: 30px; 
  `}
`;

const StyledServices = styled.div``;

const StyledText = styled.p`
  ${fontSize(15)};
  margin-bottom: 12px;
  font-weight: ${fontWeights.semibold};

  ${minBreakpointQuery.small`
    ${fontSize(16)};
    margin-bottom: 14px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 16px;
  `}

  ${minBreakpointQuery.xlarge`
    margin-bottom: 18px;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-bottom: 20px;
  `}
`;

const StyledList = styled.ul`
  ${maxBreakpointQuery.mlarge`
    margin-bottom: 12px;
  `}

  ${minBreakpointQuery.mlarge`
    column-gap: 20px;
    column-count: 2;
  `}

  ${minBreakpointQuery.large`
    column-gap: 30px; 
  `}
`;

const StyledListItem = styled.li`
  ${fontSize(15)};
  margin-top: 10px;

  &:first-child {
    margin-top: 0;
  }

  ${minBreakpointQuery.small`
    ${fontSize(16)};
  `}

  ${minBreakpointQuery.mlarge`
    margin-top: 12px;
    ${fontSize(14)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(15)};
  `}

  ${minBreakpointQuery.xlarge`
    ${fontSize(16)};
  `}
  
  ${({ hasSubPlatforms }) => {
    if (hasSubPlatforms) {
      return css`
        ${minBreakpointQuery.mlarge`
          position: relative;
        `}

        &:after {
          ${minBreakpointQuery.mlarge`
            content: '';
            position: absolute;
            left: 21px;
            bottom: 0;
            width: 1px;
            height: 100%;
            border: dashed 1px ${standardColours.lighterGrey}
          `}
        }
      `;
    }
  }}
`;

const StyledIconWrapper = styled.div`
  ${maxBreakpointQuery.mlarge`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.mlarge`
    padding: 8px;
    width: 45px;
    height: 45px;
    border-radius: 50%;
    background-color: ${({ colour }) =>
      colour ? colour.hex : brandColours.primary};
    margin-right: 12px;
  `}
`;

const StyledIcon = styled.img`
  ${minBreakpointQuery.mlarge`
    width: 100%;
    height: 100%;
  `}
`;

const StyledLink = styled(Link)`
  transition: ${standardTransition('color')};

  ${minBreakpointQuery.mlarge`
    display: flex;
    align-items:center;
  `}

  &:hover {
    color: ${brandColours.tertiary};
  }
`;

const StyledBadge = styled.img`
  ${maxBreakpointQuery.mlarge`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.mlarge`
    width: 45px;
    height: 45px;
    background-color: ${brandColours.primary};
    border-radius: 4px;
    padding: 5px;
    margin-right: 12px;
    z-index: 1;
  `}
`;

const StyledSubPlatformsList = styled.ul`
  margin-top: 8px;

  ${minBreakpointQuery.mlarge`
    display: inline-block;
    margin-left: 24px;
  `}
`;

const StyledSubPlatformsItem = styled.li`
  ${fontSize(13)};
  position: relative;
  margin-top: 8px;
  padding-left: 12px;

  ${minBreakpointQuery.small`
    ${fontSize(14)};
  `}

  ${minBreakpointQuery.mlarge`
    ${fontSize(13)};
    padding-left: 32px;
  `}

  ${minBreakpointQuery.large`
    ${fontSize(14)};
  `}

  ${minBreakpointQuery.xlarge`
    ${fontSize(15)};
  `}


  &:before {
    content: '-';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);

    ${maxBreakpointQuery.mlarge`
      font-weight: ${fontWeights.bold};
    `}

    ${minBreakpointQuery.mlarge`
      content: '';
      width: 25px;
      height: 1px;
      border: dashed 1px ${standardColours.lighterGrey}
    `}
  }
`;

const StyledCta = styled.div`
  ${maxBreakpointQuery.mlarge`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.mlarge`
    padding: 45px 30px;
    background-color: ${brandColours.secondary};
    text-align: center;
    border-radius: 15px;
    display: grid;
    align-content: center;
  `}
`;

const StyledCtaText = styled.p`
  ${fontSize(18)};
  color: ${standardColours.white};
  margin-bottom: 15px;
  line-height: 1.4;

  ${minBreakpointQuery.large`
    ${fontSize(20)};
  `}

  ${minBreakpointQuery.xlarge`
    ${fontSize(22)};
  `}
`;

const StyledButton = styled(Button)`
  width: 100%;
  padding: 20px 25px;

  &:hover {
    background-color: ${standardColours.white};
    color: ${brandColours.secondary};
  }
`;

const ServicesNav = ({ services, platforms, cta }) => (
  <StyledServicesNav>
    <StyledContent>
      <StyledServices>
        <StyledText>By Service</StyledText>
        <StyledList>
          {services.map((service, id) => {
            const { title, navigationText, icon, colour } = service;

            return (
              <StyledListItem key={id}>
                <StyledLink to={service}>
                  <StyledIconWrapper colour={colour}>
                    <StyledIcon src={icon.url} alt={icon.alt} loading="lazy" />
                  </StyledIconWrapper>
                  {navigationText ? navigationText : title}
                </StyledLink>
              </StyledListItem>
            );
          })}
        </StyledList>
      </StyledServices>
      <div>
        <StyledText>
          {process.env.GATSBY_SITE_THEME === 'creative'
            ? 'By Specialism'
            : 'By Platform'}
        </StyledText>
        <StyledList>
          {platforms.map((platform, id) => {
            const { title, badge, subPlatforms } = platform;

            const sortedSubPlatforms = subPlatforms
              .slice()
              .sort((a, b) => a.position - b.position);

            return (
              <StyledListItem
                key={id}
                hasSubPlatforms={subPlatforms.length > 0}
              >
                <StyledLink to={platform}>
                  <StyledBadge src={badge.url} alt={badge.alt} loading="lazy" />
                  {title}
                </StyledLink>
                {sortedSubPlatforms.length > 0 && (
                  <StyledSubPlatformsList>
                    {sortedSubPlatforms.map((subPlatform, id) => {
                      const { title } = subPlatform;

                      return (
                        <StyledSubPlatformsItem key={id}>
                          <StyledLink to={subPlatform}>{title}</StyledLink>
                        </StyledSubPlatformsItem>
                      );
                    })}
                  </StyledSubPlatformsList>
                )}
              </StyledListItem>
            );
          })}
        </StyledList>
      </div>
      <StyledCta>
        {cta.text && <StyledCtaText>{cta.text}</StyledCtaText>}
        <StyledButton to={cta.link.pageUrl}>{cta.link.text}</StyledButton>
      </StyledCta>
    </StyledContent>
  </StyledServicesNav>
);

export default ServicesNav;
